import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import ArrowForwardSharpIcon from '@gamma/ui/icons/ArrowForwardSharpIcon';
import CrisisAlertSharpIcon from '@gamma/ui/icons/CrisisAlertSharpIcon';
import DevicesSharpIcon from '@gamma/ui/icons/DevicesSharpIcon';
import InfoSharpIcon from '@gamma/ui/icons/InfoSharpIcon';
import LocationOnSharpIcon from '@gamma/ui/icons/LocationOnSharpIcon';
import { AccordionPanel } from '@gamma/ui/layout';
import { Popover } from '@gamma/ui/overlay';
import { useContext } from 'react';
import { useNavigate } from 'react-router';
import { ROUTES } from '@investigator/shared/constants';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers/AuthProvider';
import { useDateRangeQueryString } from '@investigator/shared/hooks';
import { EntityAnalysisPopover } from '../../../../../entities/components';
import { hasAnyEDRIntegrationEnabledForTenant } from '../../../../../integrations/pages';
import { LocalDetection } from '@investigator/features/detections/queries';

const { systemSettingsIntegrationsConfig } = ROUTES;

const { detection: detectionText, entityEDR } = i18n.pages.detections;

const { source, destination, entity } = detectionText;

export interface DetectionEntitiesProps {
  isADV2?: boolean;
  detectionInfo?: LocalDetection;
}

export const DetectionEntities = ({
  detectionInfo,
  isADV2,
}: DetectionEntitiesProps) => {
  const navigate = useNavigate();

  const { isOrgTenant, userPrivileges = {} } = useContext(AuthContext);

  const { start, end, sevenDayStart, sevenDayEnd } = useDateRangeQueryString();

  // Use URL params if valid, otherwise fallback to stable 7-day values from hook
  const validStart = start && !isNaN(start) ? start : sevenDayStart;
  const validEnd = end && !isNaN(end) ? end : sevenDayEnd;

  const {
    paginated_destination_entities,
    paginated_source_entities,
    tenant = '',
  } = detectionInfo || {};

  const integrationActive = hasAnyEDRIntegrationEnabledForTenant(
    tenant,
    userPrivileges,
  );

  const sourceEntities = paginated_source_entities?.alert_entities;
  const totalSourceEntities = paginated_source_entities?.total_items;
  const destinationEntities = paginated_destination_entities?.alert_entities;
  const totalDestinationEntities = paginated_destination_entities?.total_items;

  const hasSource = Boolean(sourceEntities?.length);
  const hasDestination = Boolean(destinationEntities?.length);

  const NUM_ENTITIES_SHOWN = 10;

  return (
    <AccordionPanel
      w="100%"
      title={
        <HStack>
          {isADV2 && <DevicesSharpIcon />}
          <Text>{entity}</Text>
          {!integrationActive && (
            <Box h="20px" data-testid="entity-enrichment-info-tooltip">
              <Popover
                isLazy
                width="320px"
                trigger="hover"
                placement="right"
                title={entityEDR.entityEnrichment}
                body={
                  isOrgTenant
                    ? entityEDR.getEntityEnrichmentOrg
                    : entityEDR.getEntityEnrichment
                }
                footer={
                  !isOrgTenant && (
                    <Button
                      size="md"
                      variant="solid"
                      colorScheme="blue"
                      data-testid="entity-enrichment-info-button"
                      rightIcon={<ArrowForwardSharpIcon />}
                      onClick={() => {
                        navigate({
                          pathname: systemSettingsIntegrationsConfig,
                        });
                      }}
                    >
                      {entityEDR.integrationPage}
                    </Button>
                  )
                }
              >
                <InfoSharpIcon color="text.secondary" />
              </Popover>
            </Box>
          )}
        </HStack>
      }
      reduceMotion={true}
      storageKey="detection-details-entity"
    >
      <HStack alignItems="start">
        <VStack w="50%" alignItems="start">
          <HStack>
            <CrisisAlertSharpIcon
              size="lg"
              color={hasSource ? 'text.primary' : 'gray'}
            />
            <Text
              as="h5"
              textStyle="h5"
              color={hasSource ? 'text.primary' : 'gray'}
            >
              {source}
              {sourceEntities &&
              sourceEntities.length !== 1 &&
              totalSourceEntities
                ? ` (${sourceEntities?.length}${totalSourceEntities > sourceEntities?.length ? ` of ${totalSourceEntities}` : ''})`
                : ''}
            </Text>
          </HStack>
          {sourceEntities?.slice(0, NUM_ENTITIES_SHOWN).map((entity, index) => {
            if (!entity) return null;
            const detectionSummary = detectionInfo?.tenant_info
              ? {
                  detection_id: detectionInfo.detection_id,
                  entity_category:
                    entity.entity_category === null
                      ? undefined
                      : entity.entity_category,
                  entity_id: entity.entity_id ?? '',
                  entity_name: entity.entity_name ?? '',
                  entity_type: entity.entity_type ?? '',
                  detection_count: 1,
                  total_detections: 1,
                  total_items: 1,
                  severity: detectionInfo?.rank?.severity || 0,
                  last_seen: detectionInfo?.latest_start_timestamp || 0,
                  first_seen: detectionInfo?.earliest_start_timestamp || 0,
                  tenant_entity: detectionInfo?.tenant_info?.tenant_id || '',
                  content_id: detectionInfo?.alert_info?.content_id || '',
                  alert_name: detectionInfo?.alert_info?.alert_name || '',
                  alert_type: detectionInfo?.alert_info?.alert_type || '',
                  tenant: detectionInfo?.tenant_info?.tenant_id || undefined,
                  tenant_info: detectionInfo?.tenant_info,
                  tenant_detection:
                    detectionInfo?.detection_keys?.tenant_detection,
                  agentic_triage_report_id:
                    detectionInfo?.agentic_triage_report_id,
                }
              : null;
            return (
              detectionSummary && (
                <EntityAnalysisPopover
                  key={index}
                  isStandalone={true}
                  detectionSummary={detectionSummary}
                  requireSingleDetectionForSuppression={false}
                  start={validStart}
                  end={validEnd}
                />
              )
            );
          })}
        </VStack>
        <VStack w="50%" alignItems="start">
          <HStack>
            <LocationOnSharpIcon
              size="lg"
              color={hasDestination ? 'text.primary' : 'gray'}
            />
            <Text
              as="h5"
              textStyle="h5"
              color={hasDestination ? 'text.primary' : 'gray'}
            >
              {destination}
              {destinationEntities &&
              destinationEntities.length !== 1 &&
              totalDestinationEntities
                ? ` (${destinationEntities.length}${totalDestinationEntities > destinationEntities.length ? ` of ${totalDestinationEntities}` : ''})`
                : ''}
            </Text>
          </HStack>
          <HStack flexFlow="row wrap">
            {destinationEntities
              ?.slice(0, NUM_ENTITIES_SHOWN)
              .map((entity, index) => {
                if (!entity) return null;
                const detectionSummary = detectionInfo?.tenant_info
                  ? {
                      detection_id: detectionInfo.detection_id,
                      entity_category:
                        entity.entity_category === null
                          ? undefined
                          : entity.entity_category,
                      entity_id: entity.entity_id ?? '',
                      entity_name: entity.entity_name ?? '',
                      entity_type: entity.entity_type ?? '',
                      detection_count: 1,
                      total_detections: 1,
                      total_items: 1,
                      severity: detectionInfo?.rank?.severity || 0,
                      last_seen: detectionInfo?.latest_start_timestamp || 0,
                      first_seen: detectionInfo?.earliest_start_timestamp || 0,
                      tenant_entity:
                        detectionInfo?.tenant_info?.tenant_id || '',
                      content_id:
                        detectionInfo?.alert_info?.content_id || undefined,
                      alert_name:
                        detectionInfo?.alert_info?.alert_name || undefined,
                      alert_type:
                        detectionInfo?.alert_info?.alert_type || undefined,
                      tenant:
                        detectionInfo?.tenant_info?.tenant_id || undefined,
                      tenant_info: detectionInfo?.tenant_info,
                      tenant_detection:
                        detectionInfo?.detection_keys?.tenant_detection,
                      agentic_triage_report_id:
                        detectionInfo?.agentic_triage_report_id,
                    }
                  : null;
                return (
                  detectionSummary && (
                    <EntityAnalysisPopover
                      key={index}
                      isStandalone={true}
                      detectionSummary={detectionSummary}
                      requireSingleDetectionForSuppression={false}
                      start={validStart}
                      end={validEnd}
                    />
                  )
                );
              })}
          </HStack>
        </VStack>
      </HStack>
    </AccordionPanel>
  );
};
